import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import AIChartTooltip from "../components/AIChartTooltip";
import LiftMethodSummary from "../components/LiftMethodSummary";
import InjectionOptimizationPanel from "../components/InjectionOptimizationPanel";
import ForecastPanel from "../components/ForecastPanel";
import PlotSummary from "../components/PlotSummary";
import {
  ScatterChart, Scatter, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ZAxis, Cell,
} from "recharts";
import { useTheme, getColors } from "../ThemeContext";   // <-- added

import { API } from "../config/api";

const C = {
  bg:"#050b14", panel:"#080f1e", card:"#0b1525", border:"#0f2040", border2:"#162a4a",
  cyan:"#00d4ff", teal:"#00ffcc", amber:"#ffb84d", pink:"#ff4da6", purple:"#a855f7",
  blue:"#3b9eff", red:"#ff3d5a", green:"#00ff88", orange:"#ff7c2a",
  text:"#e2eaf8", muted:"#4a6080", muted2:"#2a3f60",
};
function applyTheme(colors) { Object.assign(C, colors); }   // <-- added
const MONO = "'Space Mono', monospace";
const SANS = "'Syne', sans-serif";
/* Margins are kept small on purpose: the axis titles now live INSIDE the axis area
   (XAxis height / YAxis width below), so nothing is drawn outside the chart box and clipped. */
const CHART_MARGIN = { top: 8, right: 14, left: 8, bottom: 8 };
const SCATTER_CHART_MARGIN = { top: 8, right: 14, left: 8, bottom: 8 };
const X_AXIS_HEIGHT = 52;
const Y_AXIS_WIDTH = 68;
const LEGEND_PROPS = { verticalAlign:"top", align:"right", iconSize:8, wrapperStyle:{ fontFamily:MONO, fontSize:10, paddingBottom:6 } };

/* Axis title helpers, evaluated at render time so they follow the active theme colour. */
const xTitle = (value) => ({
  value, position:"insideBottom", offset:4,
  fill:C.muted, fontSize:10, fontFamily:MONO,
});
const yTitle = (value) => ({
  value, angle:-90, position:"insideLeft", offset:4,
  fill:C.muted, fontSize:10, fontFamily:MONO, style:{ textAnchor:"middle" },
});

function TTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:C.panel, border:`1px solid ${C.border2}`, borderRadius:8, padding:"8px 12px", fontFamily:MONO, fontSize:11 }}>
      {label != null && <div style={{ color:C.muted, marginBottom:4 }}>{label}</div>}
      {payload.map((p,i)=>(
        <div key={i} style={{ color:p.color }}>{p.name}: {typeof p.value==="number"?p.value.toLocaleString(undefined,{maximumFractionDigits:2}):p.value}</div>
      ))}
    </div>
  );
}
function Card({ title, subtitle, children, height, accentColor, style, summarySubtitle }) {
  const ac = accentColor || C.cyan;
  return (
    <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:"20px 16px", position:"relative", boxSizing:"content-box", ...style }}>
      <div style={{ position:"absolute", top:0, left:20, right:20, height:2, background:`linear-gradient(to right, transparent, ${ac}88, transparent)` }}/>
      <div style={{ marginBottom:14 }}>
        <div style={{ fontFamily:SANS, fontWeight:700, fontSize:13, color:C.text }}>{title}</div>
        {subtitle && <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, marginTop:2 }}>{subtitle}</div>}
      </div>
      {height ? <div style={{ height, display:"flex", flexDirection:"column" }}>{children}</div> : children}
      {height && (
        <PlotSummary inline title={title} subtitle={summarySubtitle || (typeof subtitle==="string" ? subtitle : "")} accent={ac}>{children}</PlotSummary>
      )}
    </div>
  );
}
function KPI({ label, value, unit, color }) {
  return (
    <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:"16px 18px" }}>
      <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, textTransform:"uppercase", letterSpacing:0.5 }}>{label}</div>
      <div style={{ fontFamily:MONO, fontSize:24, fontWeight:700, color:color||C.cyan, marginTop:6 }}>
        {value == null ? "-" : value}{unit ? <span style={{ fontSize:12, color:C.muted, marginLeft:4 }}>{unit}</span> : null}
      </div>
    </div>
  );
}

function ruleStatusColor(status) {
  return { pass:C.green, marginal:C.amber, fail:C.red }[status] || C.muted;
}
function RuleStatusIcon({ status, color }) {
  if (status === "pass") return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
  );
  if (status === "marginal") return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg>
  );
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
  );
}
function RulesPanel({ rules, accentColor }) {
  if (!rules || rules.length === 0) return null;
  const passCount = rules.filter(r=>r.status==="pass").length;
  const margCount = rules.filter(r=>r.status==="marginal").length;
  const failCount = rules.filter(r=>r.status==="fail").length;
  return (
    <Card title="Physics-Based Rules Validation" subtitle="Hard physical constraints checked against every cycle" accentColor={accentColor||C.orange}>
      <div style={{ display:"flex", gap:16, marginBottom:14 }}>
        <span style={{ fontFamily:MONO, fontSize:10, color:C.green }}>◆ Pass: {passCount}</span>
        <span style={{ fontFamily:MONO, fontSize:10, color:C.amber }}>▲ Marginal: {margCount}</span>
        <span style={{ fontFamily:MONO, fontSize:10, color:C.red }}>■ Fail: {failCount}</span>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        {rules.map(r => {
          const sc = ruleStatusColor(r.status);
          return (
            <div key={r.id} style={{ background:C.panel, border:`1px solid ${C.border}`, borderRadius:8, padding:"10px 14px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", marginBottom:4 }}>
                <RuleStatusIcon status={r.status} color={sc}/>
                <span style={{ fontFamily:SANS, fontWeight:700, fontSize:12, color:C.text }}>{r.name}</span>
                <span style={{ fontFamily:MONO, fontSize:8, letterSpacing:1, textTransform:"uppercase", color:sc, background:`${sc}18`, border:`1px solid ${sc}44`, borderRadius:3, padding:"2px 7px" }}>{r.status}</span>
                {r.equation && <span style={{ fontFamily:MONO, fontSize:9, color:C.cyan, background:`${C.cyan}12`, border:`1px solid ${C.cyan}33`, borderRadius:3, padding:"2px 7px" }}>{r.equation}</span>}
                <span style={{ fontFamily:MONO, fontSize:10, color:C.cyan, marginLeft:"auto" }}>{r.value}</span>
              </div>
              <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, lineHeight:1.6 }}>{r.explanation}</div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export default function GAPLAnalysisPage() {
  const { isDark } = useTheme();          
  applyTheme(getColors(isDark));         
  const [wells, setWells] = useState([]);
  const [selectedWell, setSelectedWell] = useState("");
  const [data, setData] = useState(null);
  const [physics, setPhysics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/injection/wells`)
      .then(r => { const next = (r.data.wells || []).filter(w => w.well_id.includes("GAPL")); setWells(next); if (next.length) setSelectedWell(next[0].well_id); })
      .catch(() => setWells([]));
  }, []);

  const load = useCallback(() => {
    if (!selectedWell) return;
    setLoading(true); setError(null);
    Promise.all([
      axios.get(`${API}/gapl/analysis`, { params: { well: selectedWell } }),
      axios.get(`${API}/gapl/physics-validation`, { params: { well: selectedWell } }),
    ])
      .then(([r, p]) => { setData(r.data); setPhysics(p.data); })
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [selectedWell]);

  useEffect(() => { load(); }, [load]);

  const perCycle = data?.per_cycle || [];
  const stats = data?.stats || {};

  return (
    <div style={{ fontFamily:SANS }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20, flexWrap:"wrap", gap:12 }}>
        <select value={selectedWell} onChange={e=>setSelectedWell(e.target.value)} style={selStyle()}>
          {wells.map(w => <option key={w.well_id} value={w.well_id}>{w.well_id}</option>)}
        </select>
      </div>

      {loading && <div style={{ color:C.muted, fontFamily:MONO }}>Loading…</div>}
      {error && <div style={{ color:C.red, fontFamily:MONO }}>{error}</div>}

      {!loading && !error && data && (
        <>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(190px, 1fr))", gap:14, marginBottom:20 }}>
            <KPI label="Good Arrival Rate" value={stats.good_arrival_rate_pct} unit="%" color={C.green}/>
            <KPI label="Avg Arrival Velocity" value={stats.arrival_velocity?.mean} unit="ft/min" color={C.amber}/>
            <KPI label="Avg Injection Duration" value={stats.injection_duration_min?.mean} unit="min" color={C.cyan}/>
            <KPI label="Injection↔Arrival Corr." value={stats.injection_arrival_correlation} color={C.purple}/>
          </div>
          <ForecastPanel endpoint="gapl/forecast" params={{ well: selectedWell, n: 5 }} C={C} MONO={MONO} SANS={SANS} plotSummary/>


          <LiftMethodSummary
            accentColor={C.cyan}
            items={[
              { label:"Gas Injection Rate", value:stats.injection_rate_mcfd?.mean, unit:"MCFD", color:C.cyan, detail:"average gas injected per day" },
              { label:"Injection Pressure", value:stats.injection_pressure_psi?.mean, unit:"psi", color:C.amber, detail:"average pressure during injection" },
              { label:"Gas Injection Volume", value:stats.gas_injection_volume_mcf?.mean, unit:"MCF", color:C.teal, detail:"average gas volume per cycle" },
              { label:"Cycles Reviewed", value:stats.total_cycles, unit:"cycles", color:C.purple, detail:"selected data set" },
            ]}
            insight={`Across the selected GAPL cycles, the average gas injection rate was ${stats.injection_rate_mcfd?.mean ?? "-"} MCFD across ${stats.total_cycles ?? "-"} cycles, with an average injection pressure of ${stats.injection_pressure_psi?.mean ?? "-"} psi.`}
          />

          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(min(100%, 320px), 1fr))", gap:18, marginBottom:18 }}>
            <Card title="Injection Window vs Arrival Velocity" subtitle="Per cycle" summarySubtitle="Per cycle scatter of injection duration (min) against plunger arrival velocity (ft/min) for a GAPL gas-assisted plunger lift well; green points = plunger arrived, red points = no arrival" accentColor={C.cyan} height={300}>
              <div style={{ display:"flex", gap:14, marginBottom:8, flexShrink:0 }}>
                <div style={{ display:"flex", alignItems:"center", gap:6, fontFamily:MONO, fontSize:9, color:C.text, background:`${C.green}18`, border:`1px solid ${C.green}44`, borderRadius:6, padding:"3px 8px" }}>
                  <span style={{ width:8, height:8, borderRadius:"50%", background:C.green, display:"inline-block", flexShrink:0 }}/>
                  Plunger Arrived
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:6, fontFamily:MONO, fontSize:9, color:C.text, background:`${C.red}18`, border:`1px solid ${C.red}44`, borderRadius:6, padding:"3px 8px" }}>
                  <span style={{ width:8, height:8, borderRadius:"50%", background:C.red, display:"inline-block", flexShrink:0 }}/>
                  No Arrival
                </div>
              </div>
              <div style={{ flex:1, minHeight:0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart margin={SCATTER_CHART_MARGIN}>
                    <CartesianGrid stroke={C.border} strokeDasharray="3 3"/>
                    <XAxis
                      type="number"
                      dataKey="InjectionDuration_min"
                      name="Injection Duration"
                      domain={["dataMin", "dataMax"]}
                      tickCount={6}
                      height={X_AXIS_HEIGHT}
                      tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }}
                      tickLine={false}
                      label={xTitle("Injection Duration (min)")}
                    />
                    <YAxis
                      type="number"
                      dataKey="ArrivalVelocity_ftmin"
                      name="Arrival Velocity"
                      domain={["dataMin", "dataMax"]}
                      tickCount={6}
                      tickFormatter={(v) => Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      width={Y_AXIS_WIDTH}
                      tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }}
                      tickLine={false}
                      axisLine={false}
                      label={yTitle("Arrival Velocity (ft/min)")}
                    />
                    <ZAxis range={[40,40]}/>
                    <Tooltip
                      trigger="hover"
                      isAnimationActive={false}
                      content={
                        <AIChartTooltip
                          fieldKey="ArrivalVelocity_ftmin"
                          contextData={{ chart:"Injection Window vs Arrival Velocity" }}
                          getStatusField={(payload) => {
                            const point = payload.find(p => p.payload && "PlungerArrived" in p.payload)?.payload;
                            if (!point) return null;
                            const status = point.PlungerArrived
                              ? "GOOD: plunger arrived successfully this cycle"
                              : "CRITICAL: plunger failed to arrive this cycle";
                            return { field:"PlungerArrived", label:"PlungerArrived → STATUS", value: status, unit:"" };
                          }}
                        />
                      }
                    />
                    <Scatter data={perCycle} fill={C.cyan} isAnimationActive={false}>
                      {perCycle.map((d,i)=><Cell key={i} fill={d.PlungerArrived ? C.green : C.red}/>)}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card title="Injection Window % of Shut-In" subtitle="Per cycle trend" accentColor={C.amber} height={300}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={perCycle} margin={CHART_MARGIN}>
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
                  <XAxis dataKey="CycleSerialNumber" height={X_AXIS_HEIGHT} tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} label={xTitle("Cycle")}/>
                  <YAxis width={Y_AXIS_WIDTH} tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false} label={yTitle("Injection Window (%)")}/>
                  <Tooltip
                    trigger="hover"
                    isAnimationActive={false}
                    content={<AIChartTooltip fieldKey="injection_window_pct_of_shutin" contextData={{ chart:"Injection Window % of Shut-In" }} />}
                  />
                  <Legend {...LEGEND_PROPS}/>
                  <Line type="monotone" dataKey="injection_window_pct_of_shutin" name="Injection Window %" stroke={C.amber} strokeWidth={2} dot={false} activeDot={{ r: 5 }} isAnimationActive={false}/>
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </div>

          <Card title="Gas Injection Volume per Cycle" accentColor={C.teal} height={280}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={perCycle} margin={CHART_MARGIN}>
                <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
                <XAxis dataKey="CycleSerialNumber" height={X_AXIS_HEIGHT} tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} label={xTitle("Cycle")}/>
                <YAxis width={Y_AXIS_WIDTH} tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false} label={yTitle("Gas Injection Volume (MCF)")}/>
                <Tooltip
                  trigger="hover"
                  isAnimationActive={false}
                  content={<AIChartTooltip fieldKey="GasInjectionVolume_MCF" contextData={{ chart:"Gas Injection Volume per Cycle" }} />}
                />
                <Bar dataKey="GasInjectionVolume_MCF" name="Gas Injection Volume (MCF)" fill={C.teal} radius={[3,3,0,0]} isAnimationActive={false}/>
              </BarChart>
            </ResponsiveContainer>
          </Card>



          <Card title="Gas Injection Rate per Cycle" subtitle="Actual annulus gas injection rate" accentColor={C.cyan} height={280} style={{ marginTop: 18 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={perCycle} margin={CHART_MARGIN}>
                <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
                <XAxis dataKey="CycleSerialNumber" height={X_AXIS_HEIGHT} tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} label={xTitle("Cycle")}/>
                <YAxis width={Y_AXIS_WIDTH} tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false} label={yTitle("Injection Rate (MCFD)")}/>
                <Tooltip
                  trigger="hover"
                  isAnimationActive={false}
                  content={<AIChartTooltip fieldKey="AnnulusGasRate_MCFD" contextData={{ chart:"Gas Injection Rate per Cycle" }} />}
                />
                <Line type="monotone" dataKey="AnnulusGasRate_MCFD" name="Injection Rate (MCFD)" stroke={C.cyan} strokeWidth={2} dot={false} activeDot={{ r:5 }} isAnimationActive={false}/>
              </LineChart>
            </ResponsiveContainer>
          </Card>
          <InjectionOptimizationPanel method="GAPL" data={data} C={C} MONO={MONO} SANS={SANS} selectedWell={selectedWell}/>
          <div style={{ marginTop:18 }}><RulesPanel rules={physics?.rules} accentColor={C.orange}/></div>
        </>
      )}
    </div>
  );
}

function selStyle() {                    
  return {
    background:C.panel, color:C.text, border:`1px solid ${C.border2}`, borderRadius:8,
    padding:"8px 12px", fontFamily:MONO, fontSize:11, cursor:"pointer",
  };
}